import React, { useState } from "react";
import { FailedEvent } from "../../../types/outbox.types";
import { format, parseISO } from "date-fns";

interface FailedEventsListProps {
  events: FailedEvent[];
  onReplay: (eventId: string) => void;
  replayingEventId: string | null;
}

const formatDateTime = (isoString: string): string => {
  try {
    const date = parseISO(isoString);
    return format(date, "yyyy-MM-dd HH:mm:ss");
  } catch {
    return isoString;
  }
};

const JsonSection: React.FC<{
  label: string;
  data: Record<string, unknown> | null;
  variant?: "error" | "default";
}> = ({ label, data, variant = "default" }) => {
  if (data === null) {
    return (
      <div>
        <h4 className={`text-sm font-semibold mb-2 ${variant === "error" ? "text-red-700" : "text-neutral-700"}`}>
          {label}
        </h4>
        <div className="text-xs text-neutral-400 italic p-3">
          Brak danych
        </div>
      </div>
    );
  }

  const bgClass = variant === "error" ? "bg-red-50 border-red-100" : "bg-neutral-50 border-neutral-200";

  return (
    <div>
      <h4 className={`text-sm font-semibold mb-2 ${variant === "error" ? "text-red-700" : "text-neutral-700"}`}>
        {label}
      </h4>
      <pre className={`text-xs font-mono ${bgClass} border p-3 rounded overflow-auto max-h-96`}>
        {JSON.stringify(data, null, 2)}
      </pre>
    </div>
  );
};

export const FailedEventsList: React.FC<FailedEventsListProps> = ({
  events,
  onReplay,
  replayingEventId,
}) => {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const toggleExpanded = (eventId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(eventId)) {
        next.delete(eventId);
      } else {
        next.add(eventId);
      }
      return next;
    });
  };

  const handleReplayClick = (eventId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm('Replay może spowodować duplikaty emaili/faktur. Kontynuować?')) {
      onReplay(eventId);
    }
  };

  const handleKeyDown = (eventId: string, e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      toggleExpanded(eventId);
    }
  };

  if (!events.length) {
    return (
      <div className="bg-white rounded-lg border border-neutral-200 p-6">
        <h3 className="text-lg font-semibold text-neutral-900 mb-4">
          Failed Events
        </h3>
        <div className="text-neutral-500 text-center py-8">
          Brak nieudanych eventów
        </div>
      </div>
    );
  }

  return (
    <div
      className="bg-white rounded-lg border border-neutral-200 p-6"
      data-testid="outbox-failed-list"
    >
      <h3 className="text-lg font-semibold text-neutral-900 mb-4">
        Failed Events
      </h3>

      <div className="space-y-3">
        {events.map((event) => {
          const isExpanded = expandedIds.has(event.id);
          const detailsId = `outbox-event-details-${event.id}`;

          return (
            <div
              key={event.id}
              className="border border-neutral-200 rounded-lg hover:border-neutral-400 transition-colors"
            >
              <div className="p-4 flex items-start justify-between gap-4">
                <div
                  data-testid={`outbox-event-row-${event.id}`}
                  role="button"
                  aria-expanded={isExpanded}
                  aria-controls={detailsId}
                  tabIndex={0}
                  className="flex-1 min-w-0 cursor-pointer"
                  onClick={() => toggleExpanded(event.id)}
                  onKeyDown={(e) => handleKeyDown(event.id, e)}
                >
                  <div className="flex items-start gap-2">
                    <svg
                      className={`w-4 h-4 mt-1 text-neutral-400 transition-transform flex-shrink-0 ${isExpanded ? "rotate-90" : ""}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <code className="text-sm font-mono text-neutral-900 bg-neutral-100 px-2 py-1 rounded">
                          {event.eventName}
                        </code>
                        <span className="text-xs text-neutral-500">
                          Retry: {event.retryCount}
                        </span>
                      </div>

                      <div className="text-sm text-neutral-600 mb-1">
                        <span className="font-medium">Created:</span>{" "}
                        {formatDateTime(event.createdAt)}
                      </div>

                      <div className="text-sm text-red-600 font-mono bg-red-50 p-2 rounded">
                        {event.lastError}
                      </div>
                    </div>
                  </div>
                </div>

                <button
                  data-testid={`outbox-replay-btn-${event.id}`}
                  onClick={(e) => handleReplayClick(event.id, e)}
                  disabled={replayingEventId === event.id}
                  className="flex-shrink-0 inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-primary-700 rounded-md hover:bg-primary-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:outline-none"
                >
                  {replayingEventId === event.id ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Replaying...
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-4 h-4 mr-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                        />
                      </svg>
                      Replay
                    </>
                  )}
                </button>
              </div>

              {isExpanded && (
                <div
                  id={detailsId}
                  data-testid={detailsId}
                  className="border-t border-neutral-200 p-4 space-y-4"
                >
                  <JsonSection label="Error" data={event.error} variant="error" />
                  <JsonSection label="Payload" data={event.payload} />
                  <JsonSection label="Metadata" data={event.metadata} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FailedEventsList;
